import { Injectable } from '@angular/core';
import { Model } from './models.service';

export interface ImportedModelPayload {
  name: string;
  version: string;
  user_list: any[];
  general_rules: string[];
  general_questions: { id: string; description: string }[];
  model_type: 'extraction' | 'classification';
}

@Injectable({
  providedIn: 'root',
})
export class ModelImportExportService {
  importFromFile(): Promise<ImportedModelPayload> {
    return new Promise((resolve, reject) => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.json,application/json';
      fileInput.style.display = 'none';

      fileInput.onchange = (event: any) => {
        const file = event.target.files?.[0];
        if (!file) {
          reject(new Error('Nenhum arquivo selecionado.'));
          return;
        }

        const reader = new FileReader();
        reader.onload = (e: any) => {
          try {
            const raw = JSON.parse(e.target.result as string);
            const mapped = this.mapExternalToModel(raw);
            resolve(mapped);
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error('Erro ao ler o arquivo.'));
        reader.readAsText(file);
      };

      document.body.appendChild(fileInput);
      fileInput.click();
      document.body.removeChild(fileInput);
    });
  }

  exportToFile(model: Model, fileName?: string): void {
    const exportData = this.mapModelToExternal(model);
    const jsonData = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName || exportData.listName || 'model'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private mapExternalToModel(data: any): ImportedModelPayload {
    const isClassification = Array.isArray(data.classes);

    if (isClassification) {
      const rawClasses = Array.isArray(data.classes) ? data.classes : [];
      return {
        name: data.listName || 'imported_model',
        version: '2',
        model_type: 'classification',
        user_list: rawClasses.map((c: any) => ({
          id: c.id ?? '',
          description: c.description ?? '',
          examples: Array.isArray(c.examples) ? c.examples.map((ex: any) => String(ex ?? '')) : [],
        })),
        general_rules: [],
        general_questions: [],
      };
    }

    const rawWish = Array.isArray(data.wishList) ? data.wishList : [];
    const rawRules = Array.isArray(data.generalRules) ? data.generalRules : [];
    const rawQuestions = Array.isArray(data.generalQuestions)
      ? data.generalQuestions
      : [];

    return {
      name: data.listName || 'imported_model',
      version: data.version || '2',
      model_type: 'extraction',
      user_list: rawWish,
      general_rules: rawRules.map((r: any) => String(r ?? '')),
      general_questions: rawQuestions.map((q: any) => ({
        id: q.id ?? '',
        description: q.description ?? '',
      })),
    };
  }

  private mapModelToExternal(model: Model): any {
    if (model.model_type === 'classification') {
      const cleanClasses = (items: any[]): any[] => {
        return (items || []).map((item) => ({
          id: item.id ?? '',
          description: item.description ?? '',
          examples: Array.isArray(item.examples) ? item.examples.map((ex: any) => String(ex ?? '')) : [],
        }));
      };

      return {
        listName: model.name || 'exported_model',
        classes: cleanClasses(model.user_list || []),
      };
    }

    const cleanWishList = (items: any[]): any[] => {
      return (items || []).map((item) => {
        const cleanItem: any = {
          id: item.id,
          description: item.description,
          format: item.format || '',
        };
        if (
          item.children &&
          Array.isArray(item.children) &&
          item.children.length > 0
        ) {
          cleanItem.children = cleanWishList(item.children);
        }
        return cleanItem;
      });
    };

    return {
      listName: model.name || 'exported_model',
      wishList: cleanWishList(model.user_list || []),
      generalRules: model.general_rules || [],
      generalQuestions: model.general_questions || [],
      version: model.version || '2',
    };
  }

  mapUserListToFormStructure(userList: any[] | null | undefined): any {
    if (!Array.isArray(userList)) return {};

    const result: any = {};
    userList.forEach((item, index) => {
      const key = `d_${index}`;

      const base: any = {
        id: item.id ?? '',
        description: item.description ?? '',
        format: item.format ?? '',
        showExtra: false,
      };

      if (Array.isArray(item.children) && item.children.length > 0) {
        const childrenObj: any = {};
        item.children.forEach((child: any, cIndex: number) => {
          const cKey = `c_${cIndex}`;
          childrenObj[cKey] = {
            id: child.id ?? '',
            description: child.description ?? '',
            format: child.format ?? '',
            showExtra: false,
          };
        });
        base.children = childrenObj;
      }

      result[key] = base;
    });

    return result;
  }

  mapRulesToFormStructure(rules: string[] | null | undefined): any {
    if (!Array.isArray(rules)) return {};

    const result: any = {};
    rules.forEach((rule, index) => {
      const key = `r_${index}`;
      result[key] = rule ?? '';
    });

    return result;
  }

  mapQuestionsToFormStructure(
    questions: { id: string; description: string }[] | null | undefined
  ): any {
    if (!Array.isArray(questions)) return {};

    const result: any = {};
    questions.forEach((q, index) => {
      const key = `q_${index}`;
      result[key] = {
        id: q.id ?? '',
        description: q.description ?? '',
      };
    });

    return result;
  }

  normalizeUserList(raw: any): any[] {
    if (!raw || typeof raw !== 'object') return [];

    const items = Object.values(raw) as any[];

    return items.map((item: any) => {
      const base: any = {
        id: item.id ?? '',
        description: item.description ?? '',
        format: item.format ?? '',
      };

      if (item.children && typeof item.children === 'object') {
        base.children = Object.values(item.children).map((child: any) => ({
          id: child.id ?? '',
          description: child.description ?? '',
          format: child.format ?? '',
        }));
      }

      return base;
    });
  }

  normalizeGeneralRules(raw: any): string[] {
    if (!raw || typeof raw !== 'object') return [];
    return Object.values(raw).map((v: any) => String(v ?? ''));
  }

  normalizeGeneralQuestions(
    raw: any
  ): { id: string; description: string }[] {
    if (!raw || typeof raw !== 'object') return [];

    return Object.values(raw).map((q: any) => ({
      id: q.id ?? '',
      description: q.description ?? '',
    }));
  }
}
